import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { HttpHeaders } from "@angular/common/http";
import { getEnumLookup } from "src/app/_helpers/enum-helper";
import { ControlStatusEnum } from "src/app/enums/control-status.enum";
import { ControlTypeEnum } from "src/app/enums/control-type.enum";
import {
    CustomFieldTypeEnum,
    EmployeeCustomFields,
} from "src/app/enums/customFieldType.enum";
import { Control } from "src/app/models/control.model";
import { CustomEntity } from "src/app/models/customEntity.model";
import { CustomField } from "src/app/models/customField.model";
import { Lookup } from "src/app/models/lookup.model";
import { ControlService } from "src/app/services/control.service";
import { ControlHierarchyBucketService } from "src/app/services/controlHierarchyBucket.service";
import { CustomEntityService } from "src/app/services/customEntity.service";
import { CustomObjectService } from "src/app/services/customObject.service";
import {
    FormComponent,
    IFormDialogData,
} from "src/app/shared/form/form.component";
import {
    SingleSelectionFormField,
    TextFormField,
    FormField,
} from "src/app/shared/form/formField.model";
import { ILookup } from "src/app/shared/form/lookup.model";
import nameOf from "src/app/shared/form/nameOf.helper";
import {
    buildEmployeeOptionConfig,
    EMPLOYEE_BASE_LABEL_MAP,
} from "src/app/shared/form/employee-field-helpers";
import { isCustomFieldKey } from "src/app/shared/form/custom-field-helpers";
import { EndpointsTableComponent } from "src/app/components/endpoints-table/endpoints-table.component";
@Component({
  standalone: false,
    selector: "app-control-dialog",
    templateUrl: "./../../shared/form/form.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./../../shared/form/form.component.scss",
        "./control-dialog.component.scss",
    ],
})
export class ControlDialogComponent
    extends FormComponent<Control>
    implements OnInit
{
    controlTypes: ILookup[] = getEnumLookup(ControlTypeEnum);
    controlStatuses: ILookup[] = getEnumLookup(ControlStatusEnum);
    controlHierarchyBuckets: ILookup[] = [];
    employees: Lookup[] = [];
    customObjectsList: CustomField[] = [];
    shownFields: string[] = [];
    coId: string;

    private readonly NAME_FIELDS = [
        CustomFieldTypeEnum.HrisFirstName,
        CustomFieldTypeEnum.HrisLastName,
    ];
    private readonly DEFAULT_EMPLOYEE_PAGE_SIZE = 100;

    constructor(
        dialogRef: MatDialogRef<ControlDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IFormDialogData,
        controlService: ControlService,
        private controlHierarchyBucketService: ControlHierarchyBucketService,
        private customObjectService: CustomObjectService,
        private customEntityService: CustomEntityService,
        protected injector: Injector,
    ) {
        super(dialogRef, controlService, data, "Control", injector);
    }

    ngOnInit(): void {
        this.isFormLoading = true;
        this.loadInitialData();
        this.fetchEmployees();
    }

    getNewObject(): Control {
        return new Control();
    }

    validate(): boolean {
        return true;
    }

    initModel(): void {
        if (this.isAdd) this.entity.isActive = true;
    }

    initFields(): void {
        this.fields = [
            new TextFormField({
                property: nameOf<Control>((x) => x.name),
                name: "Control Name",
                required: true,
            }),
            new TextFormField({
                property: nameOf<Control>((x) => x.code),
                name: "Code",
                required: true,
            }),
            new SingleSelectionFormField({
                property: nameOf<Control>((x) => x.controlType),
                options: this.controlTypes,
                name: "Control Type",
                required: true,
            }),
            new SingleSelectionFormField({
                property: nameOf<Control>((x) => x.controlHierarchyBucketId),
                options: this.controlHierarchyBuckets,
                name: "Hierarchy Bucket",
                required: false,
            }),
            new SingleSelectionFormField({
                property: nameOf<Control>((x) => x.ownerUserId),
                options: this.employees,
                name: "Control Owner (Select Employee)",
                cssClass: "custom-search-employee",
                required: false,
                onChange: (event: any) => this.onOwnerEmployeeChange(event),
                onSearchChange: (event: any, field: FormField) =>
                    this.updateEntities(event, field),
                optionTemplateFields: [
                    nameOf<CustomEntity>((x) => x.name),
                    nameOf<CustomEntity>((x) => x.organizationLevelName),
                ],
                optionTemplateLabelMap: { ...EMPLOYEE_BASE_LABEL_MAP },
                selectionTemplate: (opt: FormField) =>
                    this.getSelectionTemplate(opt),
                searchComponent: EndpointsTableComponent,
                additonalSearchData: { coId: this.coId },
            }),
            new TextFormField({
                property: nameOf<Control>((x) => x.ownerName),
                name: "OR Enter Control Owner Name",
                required: false,
                onChange: (event: any) => this.onOwnerNameChange(event),
            }),
            new SingleSelectionFormField({
                property: nameOf<Control>((x) => x.status),
                options: this.controlStatuses,
                name: "Status",
                required: true,
            }),
        ];

        this.updateOwnerFieldsState();
    }

    onOwnerEmployeeChange(event: any): void {
        if (event?.value) this.entity.ownerName = null;
        setTimeout(() => this.updateOwnerFieldsState(), 0);
    }

    onOwnerNameChange(event: any): void {
        if ((event?.target?.value || "").trim()) this.entity.ownerUserId = null;
        setTimeout(() => this.updateOwnerFieldsState(), 0);
    }

    updateEntities(filter: string, employeeField: FormField): void {
        if (!this.coId) return;

        this.customEntityService
            .get(
                filter,
                "id",
                "asc",
                0,
                this.DEFAULT_EMPLOYEE_PAGE_SIZE,
                "",
                new HttpHeaders({ coid: this.coId }),
            )
            .subscribe({
                next: (res) => {
                    const curr = this.entity.ownerUserId
                        ? (employeeField.options.find(
                              (opt) => opt.id === this.entity.ownerUserId,
                          ) as CustomEntity)
                        : null;
                    if (curr) res.data.push(curr);
                    this.employees = res.data;
                    if (this.fields && employeeField)
                        employeeField.options = this.employees;
                },
            });
    }

    private loadInitialData(): void {
        this.controlHierarchyBucketService.getAll().subscribe({
            next: (buckets) => {
                this.controlHierarchyBuckets = buckets;
                this.loadEntity();
            },
            error: (error) => this.handleLoadError(error),
        });
    }

    private loadEntity(): void {
        this.getEntity().subscribe({
            next: (result) => {
                this.entity = result;
                this.initModel();
                this.initFields();
            },
            error: (error) => this.handleLoadError(error),
            complete: () => (this.isFormLoading = false),
        });
    }

    private handleLoadError(error: any): void {
        this.toastService.showError(error);
        this.isFormLoading = false;
    }

    private getSelectionTemplate(opt: FormField): string {
        const property = opt.options.find(
            (res) => res.id === this.entity[opt.property],
        );
        if (!property) return "";
        const suffix =
            this.shownFields.length > 0
                ? " - " +
                  this.shownFields.map((field) => property?.[field]).join(" ")
                : "";
        return (property.name || "") + suffix;
    }

    private updateOwnerFieldsState(): void {
        const ownerUserIdField = this.fields?.find(
            (f) => f.property === nameOf<Control>((x) => x.ownerUserId),
        );
        const ownerNameField = this.fields?.find(
            (f) => f.property === nameOf<Control>((x) => x.ownerName),
        );

        if (!ownerUserIdField || !ownerNameField) return;

        ownerUserIdField.readonly = !!this.entity.ownerName?.trim();
        ownerNameField.readonly = !!this.entity.ownerUserId;
    }

    private fetchEmployees(): void {
        this.customObjectService.getAll().subscribe({
            next: (typeLookups) => this.processEmployeeTypes(typeLookups),
            error: (error) => this.handleEmployeeError(error),
        });
    }

    private processEmployeeTypes(typeLookups: any[]): void {
        const employeeType = typeLookups.find((t) => t.name === "Employee");
        if (!employeeType) {
            this.employees = [];
            return;
        }
        this.coId = employeeType.id;
        this.loadEmployeeEntities(employeeType.id);
    }

    private loadEmployeeEntities(employeeTypeId: string): void {
        this.customEntityService
            .get(
                "",
                "id",
                "asc",
                0,
                this.DEFAULT_EMPLOYEE_PAGE_SIZE,
                "",
                new HttpHeaders({ coid: this.coId }),
            )
            .subscribe({
                next: (res) =>
                    this.processEmployeeEntities(res, employeeTypeId),
                error: (error) => this.handleEmployeeError(error),
            });
    }

    private processEmployeeEntities(res: any, employeeTypeId: string): void {
        this.employees = res.data.filter(
            (e) => e.customObjectId === employeeTypeId,
        );
        const ownerField = this.fields?.find(
            (field) => field.property === nameOf<Control>((x) => x.ownerUserId),
        );

        if (ownerField) {
            ownerField.options = this.employees;
            ownerField.additonalSearchData = { coId: this.coId };
        }

        this.loadCustomObjectFields(employeeTypeId, ownerField);
    }

    private loadCustomObjectFields(
        employeeTypeId: string,
        ownerField: FormField,
    ): void {
        this.customObjectService
            .getCustomObjectFields(employeeTypeId)
            .subscribe({
                next: (fields) => this.processCustomFields(fields, ownerField),
                error: (error) => this.handleEmployeeError(error),
            });
    }

    private processCustomFields(
        fields: CustomField[],
        ownerField: FormField,
    ): void {
        this.customObjectsList = fields.filter((f) =>
            isCustomFieldKey(f.property),
        );
        this.shownFields = this.customObjectsList
            .filter((f) => this.NAME_FIELDS.includes(f.type))
            .map((f) => f.property);

        if (ownerField && Array.isArray(this.customObjectsList)) {
            ownerField.searchKeys = [
                ...this.customObjectsList
                    .filter(
                        (obj) =>
                            obj?.property && typeof obj.property === "string",
                    )
                    .map((obj) => obj.property),
                "name",
            ];

            const config = buildEmployeeOptionConfig(this.customObjectsList);
            ownerField.optionTemplateFields = config.optionTemplateFields;
            ownerField.optionTemplateLabelMap = config.optionTemplateLabelMap;
        }

        if (this.entity?.ownerUserId && this.entity.ownerName) {
            this.updateEntities(this.entity.ownerName, ownerField);
        }
    }

    private handleEmployeeError(error: any): void {
        this.toastService.showError(error);
        this.customObjectsList = [];
    }
}
